import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { isEmpty } from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { useProfitPoolOverviewContext } from '../context/ProfitPoolOverviewContext';
import fetchProfitPoolData, { PROFIT_POOL_API_LIMIT } from '../apiUtils';
import { FilterContextData } from 'contexts/FilterContext';
import { profitPoolUrls } from 'config/urls';

const useMarginPercentagebyHierarchy = () => {
    const [marginPercentageData, setMarginPercentageData] = useState<any[]>([]);
    const [minMarginPercent, setMinMarginPercent] = useState<number>(0);
    const [maxMarginPercent, setMaxMarginPercent] = useState<number>(0);
    const [minVolumeSalesEquivalent, setMinVolumeSalesEquivalent] = useState<number>(0);
    const [maxVolumeSalesEquivalent, setMaxVolumeSalesEquivalent] = useState<number>(0);
    const { selectedSubFilter } = useProfitPoolOverviewContext();
    const {
        multiSelectedTiers,
        profitPoolTierList,
        idForRestOfComponent,
        allDimAndAttributeTypeList,
        timePeriodLoading,
        timePeriodSelection,
        idForRestOfComponentLoading,
    } = useContext(FilterContextData);

    const {
        data: volumeEquivalentData,
        isPending: isPendingVolumeEquivalent,
        isError: isErrorVolumeEquivalent,
        isSuccess: isSuccessVolumeEquivalent,
        fetchNextPage: fetchNextPageVolumeEquivalent,
        hasNextPage: hasNextPageVolumeEquivalent,
        isFetchingNextPage: isFetchingNextPageVolumeEquivalent,
    } = useInfiniteQuery({
        queryKey: [
            'volume-equivalent-by-hierarchy-infi',
            selectedSubFilter?.dataKey,
            multiSelectedTiers.join(','),
            idForRestOfComponent,
            timePeriodSelection,
        ],
        queryFn: ({ pageParam, signal }) =>
            fetchProfitPoolData({
                selectedSubFilter,
                offset: pageParam as number,
                limit: PROFIT_POOL_API_LIMIT,
                kpi: 'volume_sales_equivalent',
                tierList: multiSelectedTiers,
                yearAgo: false,
                groupedByList: ['attribute'],
                dimIdsFilter: idForRestOfComponent,
                frequency: 'M',
                timePeriod: JSON.parse(timePeriodSelection),
                signal,
            }),
        getNextPageParam: (lastPage, allPages, lastPageParam) => {
            return lastPageParam + PROFIT_POOL_API_LIMIT;
        },
        initialPageParam: 0,
        enabled: Boolean(
            allDimAndAttributeTypeList &&
                'loading' in allDimAndAttributeTypeList &&
                !timePeriodLoading &&
                !idForRestOfComponentLoading &&
                timePeriodSelection,
        ),
    });

    const { data, isPending, isError, isSuccess, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
        queryKey: [
            'margin-percentage-by-hierarchy-infi',
            selectedSubFilter?.dataKey,
            multiSelectedTiers.join(','),
            idForRestOfComponent,
            timePeriodSelection,
        ],
        queryFn: ({ pageParam, signal }) =>
            fetchProfitPoolData({
                selectedSubFilter,
                offset: pageParam as number,
                limit: PROFIT_POOL_API_LIMIT,
                kpi: 'margin_percent',
                tierList: multiSelectedTiers,
                yearAgo: false,
                groupedByList: ['attribute'],
                dimIdsFilter: idForRestOfComponent,
                frequency: 'M',
                timePeriod: JSON.parse(timePeriodSelection),
                signal,
            }),
        getNextPageParam: (lastPage, allPages, lastPageParam) => {
            return lastPageParam + PROFIT_POOL_API_LIMIT;
        },
        initialPageParam: 0,
        enabled: Boolean(
            allDimAndAttributeTypeList &&
                'loading' in allDimAndAttributeTypeList &&
                !timePeriodLoading &&
                !idForRestOfComponentLoading &&
                timePeriodSelection,
        ),
    });

    const fetchCombinedNextPage = () => {
        fetchNextPage();
        fetchNextPageVolumeEquivalent();
    };

    useEffect(() => {
        if (!isPending && !isPendingVolumeEquivalent && !isFetchingNextPage && !isFetchingNextPageVolumeEquivalent) {
            if (
                !isEmpty(data?.pages) &&
                data?.pages &&
                !isEmpty(volumeEquivalentData?.pages) &&
                volumeEquivalentData?.pages
            ) {
                const allData = data.pages.flatMap((page: any) => page?.data || []);
                const allVolumeEquivalentData = volumeEquivalentData.pages.flatMap((page: any) => page?.data || []);
                let minMarginPercent = Number.POSITIVE_INFINITY;
                let maxMarginPercent = Number.NEGATIVE_INFINITY;

                let minVolumeSalesEquivalent = Number.POSITIVE_INFINITY;
                let maxVolumeSalesEquivalent = Number.NEGATIVE_INFINITY;
                const finalChartData = allData.map((item: any) => {
                    const currentMarginPercent = item.kpi_values?.margin_percentage || 0;
                    if (currentMarginPercent < minMarginPercent) minMarginPercent = currentMarginPercent;
                    if (currentMarginPercent > maxMarginPercent) maxMarginPercent = currentMarginPercent;

                    const volumeSalesEquivalent =
                        allVolumeEquivalentData.find(
                            (volumeEquivalentItem: any) =>
                                volumeEquivalentItem.attribute_ex_id === item.attribute_ex_id,
                        )?.kpi_values?.volume_sales_equivalent || 0;
                    if (volumeSalesEquivalent < minVolumeSalesEquivalent)
                        minVolumeSalesEquivalent = volumeSalesEquivalent;
                    if (volumeSalesEquivalent > maxVolumeSalesEquivalent)
                        maxVolumeSalesEquivalent = volumeSalesEquivalent;
                    return {
                        attribute_ex_id: item.attribute_ex_id,
                        attributeDesc: item.attribute_desc,
                        marginPercent: currentMarginPercent || 0,
                        'Depletion Volume': volumeSalesEquivalent || 0,
                        tierReference: item.tier,
                        tier: profitPoolTierList.find((t: any) => t.tierReference === item.tier)?.tierName,
                    };
                });
                setMarginPercentageData(finalChartData);
                setMinMarginPercent(minMarginPercent === Number.POSITIVE_INFINITY ? 0 : minMarginPercent);
                setMaxMarginPercent(maxMarginPercent === Number.NEGATIVE_INFINITY ? 0 : maxMarginPercent);
                setMinVolumeSalesEquivalent(
                    minVolumeSalesEquivalent === Number.POSITIVE_INFINITY ? 0 : minVolumeSalesEquivalent,
                );
                setMaxVolumeSalesEquivalent(
                    maxVolumeSalesEquivalent === Number.NEGATIVE_INFINITY ? 0 : maxVolumeSalesEquivalent,
                );
            } else {
                setMarginPercentageData([]);
                setMinMarginPercent(0);
                setMaxMarginPercent(0);
                setMinVolumeSalesEquivalent(0);
                setMaxVolumeSalesEquivalent(0);
            }
        }
    }, [
        data,
        isPending,
        volumeEquivalentData,
        isPendingVolumeEquivalent,
        isFetchingNextPage,
        isFetchingNextPageVolumeEquivalent,
    ]);
    return {
        data: marginPercentageData,
        isPending: isPending || isPendingVolumeEquivalent,
        isError: isError || isErrorVolumeEquivalent,
        isSuccess: isSuccess || isSuccessVolumeEquivalent,
        fetchNextPage: fetchCombinedNextPage,
        hasNextPage: hasNextPage || hasNextPageVolumeEquivalent,
        isFetchingNextPage: isFetchingNextPage || isFetchingNextPageVolumeEquivalent,
        minMarginPercent,
        maxMarginPercent,
        minVolumeSalesEquivalent,
        maxVolumeSalesEquivalent,
    };
};

export default useMarginPercentagebyHierarchy;
